import { useCallback, useEffect, useState } from 'react';
import { CompanyTabListParams, PaginatedResponse } from '../../interfaces/companyDetail.interface';

export interface UseCompanyResourceResult<T> {
  items: T[];
  count: number;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

export type CompanyResourceFetcher<T> = (
  companyId: number | string,
  params?: CompanyTabListParams,
) => Promise<PaginatedResponse<T>>;

/**
 * Generic data hook for the paginated company-detail tab endpoints. Fetches a
 * page via `fetcher`, re-fetching when the company, page, or page size changes,
 * or when `refetch` is invoked (e.g. after a create/edit/delete).
 *
 * Pass `enabled: false` to skip the request entirely — used to defer "fetch all"
 * selector loads (e.g. a form's site/application options) until the dialog that
 * needs them is actually opened, instead of firing them on tab load.
 */
export function useCompanyResource<T>(
  fetcher: CompanyResourceFetcher<T>,
  companyId: number | undefined,
  page: number,
  pageSize: number,
  enabled: boolean = true,
  search?: string,
  siteIds?: string,
): UseCompanyResourceResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (companyId === undefined) {
      setItems([]);
      setCount(0);
      return;
    }
    // Disabled: keep whatever was already fetched (so re-opening a dialog is
    // instant) and skip the request.
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);
    setError(false);

    fetcher(companyId, {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ...(search ? { search } : {}),
      // Empty string is meaningful: the filter is on and matched nothing.
      ...(siteIds !== undefined ? { site_ids: siteIds } : {}),
    })
      .then((response) => {
        if (cancelled) return;
        setItems(response.results);
        setCount(response.count);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setItems([]);
        setCount(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetcher, companyId, page, pageSize, reloadToken, enabled, search, siteIds]);

  return { items, count, loading, error, refetch };
}
